// Non-blocking analytics client for Mario IA Tech.
// A failure here must NEVER break navigation or signal generation.
// Privacy: random IDs only (no fingerprinting, no personal data).

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "mia_session_v2";
const VISITOR_KEY = "mia_visitor_id";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
let memorySession = null;

function uuid() {
  try {
    if (crypto && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function storage() {
  try {
    localStorage.setItem("__mia_t", "1");
    localStorage.removeItem("__mia_t");
    return localStorage;
  } catch (e) {
    return null;
  }
}

// Respect browser privacy signals: no persistent visitor ID.
function privacyOptOut() {
  try {
    return navigator.doNotTrack === "1" || window.doNotTrack === "1" || navigator.globalPrivacyControl === true;
  } catch (e) {
    return false;
  }
}

function currentAttribution() {
  const utm = {};
  try {
    const params = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v.slice(0, 200);
    });
  } catch (e) {}
  let referrer = null;
  try {
    if (document.referrer && new URL(document.referrer).host !== window.location.host) referrer = document.referrer;
  } catch (e) {}
  return { utm, referrer };
}

function getVisitorId() {
  if (privacyOptOut()) return null;
  const s = storage();
  if (!s) return null;
  let id = s.getItem(VISITOR_KEY);
  if (!id) {
    id = uuid();
    s.setItem(VISITOR_KEY, id);
  }
  return id;
}

// Session shared across tabs (localStorage); new session after 30 min idle or a new campaign landing.
function getSession() {
  const s = storage();
  const now = Date.now();
  let sess = memorySession;
  try {
    if (s) sess = JSON.parse(s.getItem(SESSION_KEY) || "null");
  } catch (e) {
    sess = null;
  }
  const attr = currentAttribution();
  const newCampaign = Object.keys(attr.utm).length > 0 && JSON.stringify(attr.utm) !== JSON.stringify(sess?.attr?.utm || {});
  let isNew = false;
  if (!sess || !sess.id || now - (sess.last || 0) > SESSION_TIMEOUT_MS || newCampaign) {
    sess = { id: uuid(), start: now, attr, once: [] };
    isNew = true;
  }
  sess.last = now;
  memorySession = sess;
  try {
    if (s) s.setItem(SESSION_KEY, JSON.stringify(sess));
  } catch (e) {}
  return { sess, isNew };
}

function send(payload) {
  const url = `${BACKEND}/api/events`;
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))) return;
  fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
}

function detectDevice() {
  const w = window.innerWidth || 1024;
  const ua = navigator.userAgent || "";
  if (/Mobi|Android|iPhone/i.test(ua) || w < 640) return "mobile";
  if (/iPad|Tablet/i.test(ua) || (w >= 640 && w < 1024)) return "tablet";
  return "desktop";
}

function persistOnce(sess, key) {
  sess.once = [...(sess.once || []), key];
  try {
    const s = storage();
    if (s) s.setItem(SESSION_KEY, JSON.stringify(sess));
  } catch (e) {}
}

// Send a tracking event. Fire-and-forget.
// opts.once: dedup key — the event is sent only once per session.
export function track(type, props = {}, opts = {}) {
  try {
    const { sess, isNew } = getSession();
    if (opts.once && (sess.once || []).includes(opts.once)) return;
    const base = {
      session_id: sess.id,
      visitor_id: getVisitorId(),
      device: detectDevice(),
      referrer: sess.attr?.referrer || null,
      utm: sess.attr?.utm || {},
    };
    if (isNew && type !== EVENTS.SITE_ACCESS) {
      send({ ...base, event_id: uuid(), type: EVENTS.SITE_ACCESS, source_step: "session_start" });
      persistOnce(sess, "site_access");
    }
    if (opts.once) persistOnce(sess, opts.once);
    send({ ...base, event_id: uuid(), type, ...props });
  } catch (e) {
    // swallow — analytics must never throw
  }
}

export const EVENTS = {
  SITE_ACCESS: "site_access",
  PLATFORM_SELECT: "platform_select",
  PROVIDER_SELECT: "provider_select",
  GAME_OPEN: "game_open",
  SIGNAL_VIEW: "signal_view",
  PLATFORM_EXIT_CLICK: "platform_exit_click",
  WHATSAPP_CLICK: "whatsapp_click",
  TELEGRAM_CLICK: "telegram_click",
  GAME_NAME_COPY: "game_name_copy",
};
